import React, {useContext} from "react";
import {useMutation, useQuery} from "@apollo/client";
import {Constants} from "@unibuddy/intl";
import {Redirect, useHistory, useLocation} from "react-router-dom";
import intersectionBy from "lodash/intersectionBy";

import useAuth from "shared/Auth/hooks/useAuth";
import AppLocalesQuery from "shared/Translations/AppLocalesQuery";
import {rootURL} from "shared/Utils/WhereAmI";
import updateApplicantMutation from "shared/Applicant/mutations/updateApplicantMutation";
import linkWithUniversityMutation from "shared/Applicant/LinkWithUniversity/mutations/linkWithUniversityMutation";
import {mapValuesToPayload} from "shared/Applicant/LinkWithUniversity/containers/utils";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {setMe} from "shared/Auth/actions/authActions";
import {SourceTrackingContext} from "shared/SourceTracking/SourceTrackingProvider/SourceTrackingProvider";

import {getCountriesQuery} from "../data/getCountriesQuery";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {getSSONextPath} from "../../SSO/utils";
import {RegisterTwoFormValues, RegisterPage2Form} from "./RegisterPage2Form";
import {Locale} from "../../../../types/generated";
import {getReduxDispatch} from "../../../../App";
import {useEmbeddedEvents} from "../../Chat/hooks/useEmbeddedEvents";
import sharedEmbeddedEvents from "../../../../shared/EmbeddedEvents/constants/EmbeddedEvents";

export function RegisterPage2FormHandler() {
    const {authState} = useAuth();
    const {slug} = useUniversity();
    const location = useLocation();
    const {trackEvent} = useAnalytics();
    const dispatch = getReduxDispatch();
    const history = useHistory();
    const sourceTracking = useContext(SourceTrackingContext);
    const postEmbeddedMessage = useEmbeddedEvents();

    const [updateMyUniversityLink, {error: linkMutationError}] = useMutation(
        linkWithUniversityMutation,
    );
    const [updateApplicant, {error: updateMutationError}] = useMutation(updateApplicantMutation);

    // Bundle queries together
    const countriesQuery = useQuery(getCountriesQuery);
    const emailTemplatesAppLocalesQuery = useQuery(AppLocalesQuery, {
        variables: {appName: Constants.APP_NAMES.EMAIL_TEMPLATES},
    });
    const notificationsAppLocalesQuery = useQuery(AppLocalesQuery, {
        variables: {appName: Constants.APP_NAMES.NOTIFICATIONS},
    });

    // Handle SSO Redirect
    const applicantUniversities = authState?.me?.applicant?.applicantUniversities;
    const currentUser = authState?.me?.anyUser;
    const hasCompletedStage2 = applicantUniversities?.some(
        (applicantUni: {university: {slug: string}; setupComplete: boolean; delinked: boolean}) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return (
                applicantUni.university.slug === slug &&
                applicantUni.setupComplete &&
                !applicantUni.delinked
            );
        },
    );

    if (hasCompletedStage2) {
        const nextPath = getSSONextPath();
        return <Redirect to={nextPath ?? `${rootURL(location.pathname)}/buddies`} />;
    }

    // Bundle loading states together
    const loading =
        countriesQuery.loading ||
        notificationsAppLocalesQuery.loading ||
        emailTemplatesAppLocalesQuery.loading;

    // Bundle error states together
    const error =
        countriesQuery.error ||
        notificationsAppLocalesQuery.error ||
        emailTemplatesAppLocalesQuery.error ||
        linkMutationError ||
        updateMutationError;

    // Bundle app locale data handling together
    const allCountries = countriesQuery.data?.allCountries ?? [];
    const emailTemplateLocalesData = emailTemplatesAppLocalesQuery.data?.appLocales?.locales ?? [];
    const emailTemplateLocales: Locale[] = emailTemplateLocalesData.map((item: Locale) => ({
        id: item.id,
        locale: item.locale,
        name: item.name,
    }));
    const notificationLocalesData = notificationsAppLocalesQuery.data?.appLocales?.locales ?? [];
    const notificationLocales: Locale[] = notificationLocalesData.map((item: Locale) => ({
        id: item.id,
        locale: item.locale,
        name: item.name,
    }));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const applicableLocales: Locale[] = intersectionBy(
        notificationLocales,
        emailTemplateLocales,
        "locale",
    );

    const handleSubmit = async (values: RegisterTwoFormValues): Promise<void> => {
        const rawValues: RegisterTwoFormValues = {...values};
        // We don't want to process serverError as a custom attribute
        delete rawValues.serverError;
        delete rawValues.firstName;
        delete rawValues.lastName;
        try {
            const sourceTrackingParams = JSON.stringify(sourceTracking.getSourceTrackingParams());

            const payload = mapValuesToPayload({
                ...rawValues,
                universitySlug: slug,
                sourceTrackingParams,
                // TODO: Remove the following fields once the old register pages are removed
                unibuddyPrivacyChecked: false,
                universityPrivacyChecked: false,
                privacyPolicy: false,
            });

            if (
                !(
                    currentUser?.firstName === values?.firstName &&
                    currentUser?.lastName === values?.lastName
                )
            ) {
                // Ensure that the name is updated from second registration as sometimes SSO doesn't provide it
                const {data} = await updateApplicant({
                    variables: {firstName: values?.firstName, lastName: values?.lastName},
                });
                const {me} = data?.updateApplicant ?? {};
                dispatch(setMe(me));
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const {data} = await updateMyUniversityLink(payload);

            trackEvent(sharedEvents.prospectSignedUp.name);
            postEmbeddedMessage(sharedEmbeddedEvents.prospectSignupComplete.name, {});

            const {me} = data?.updateMyUniversityLink ?? {};

            dispatch(setMe(me));

            // Handle redirect
            const rootUrl = rootURL(location.pathname);
            const ssoNextPathname = getSSONextPath();
            const redirectUrl =
                location?.state?.nextPathname || ssoNextPathname || `${rootUrl}/buddies`;
            history.replace({pathname: redirectUrl, search: location.search});
        } catch (e) {
            console.error(e);
        }
    };

    if (loading) return <p>Loading...</p>;

    if (error) return <p>Error</p>;

    return (
        <RegisterPage2Form
            handleSubmit={handleSubmit}
            allCountries={allCountries}
            applicableLocales={applicableLocales}
            isSsoUser={authState?.tokenScheme === "Bearer"}
            initialValues={{
                acceptMarketing: false,
                serverError: "",
                firstName: currentUser?.firstName ?? "",
                lastName: currentUser?.lastName ?? "",
            }}
        />
    );
}
